import {
  useCallback,
  useRef,
  useState,
  type InputHTMLAttributes,
  ChangeEvent,
  RefObject,
} from 'react';
import {safeCaptureException} from '../utils/sentry';
import {useTranslation} from 'react-i18next';
import {IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB, getFileSizeMB} from '@chekin/core';
import {fileToDataUrl} from '../utils/iv/compressPhoto';

const ALLOWED_FORMATS = /(\.jpg|\.jpeg|\.png)$/i;
const ACCEPT = 'image/png,image/jpeg';

type UseImageFileUploadParams = {
  onImage: (image: string) => void;
};

type UseImageFileUploadResult = {
  open: () => void;
  error: string | null;
  reset: () => void;
  inputRef: RefObject<HTMLInputElement>;
  inputProps: InputHTMLAttributes<HTMLInputElement>;
};

export function useImageFileUpload({
  onImage,
}: UseImageFileUploadParams): UseImageFileUploadResult {
  const {t} = useTranslation('identity-verification');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const reset = useCallback(() => {
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0];
      event.currentTarget.value = '';
      if (!file) return;

      const fileSize = getFileSizeMB(file);

      if (!ALLOWED_FORMATS.test(file.name)) {
        setError(t('incorrect_file_type_only_file_types_allowed'));
        return;
      }
      if (!fileSize || fileSize > IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB) {
        setError(
          t('max_file_size_is_number_mb', {
            number: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
          }),
        );
        return;
      }

      setError(null);
      fileToDataUrl(file)
        .then(onImage)
        .catch(err => {
          safeCaptureException(err, 'Reading the uploaded photo');
          setError(t('failed_to_process_image'));
        });
    },
    [onImage, t],
  );

  return {
    open,
    error,
    reset,
    inputRef,
    inputProps: {
      type: 'file',
      accept: ACCEPT,
      onChange: handleChange,
      className: 'hidden',
    },
  };
}
